/**
 * Page Paramètres > Contrats formateurs (Rafik 03/06/2026, refonte form-based).
 *
 * Flow : tu choisis (type contrat × intervenant × apprenant si mission)
 * → bouton "Générer" → backend pipeline (docxtemplater + Drive + PDF + email
 * à andalous.elearning@gmail.com) → tu reçois le PDF par mail prêt à signer
 * et forwarder au formateur.
 *
 * Historique des contrats générés en dessous (sortable, filterable).
 */
import { useMemo, useState } from 'react';
import { FileText, Check, AlertCircle, Loader2, ExternalLink, Mail, Send } from 'lucide-react';
import {
  useContratsFormateurList,
  useGenerateContratCadre,
  useGenerateContratMission,
  type FormateurRow,
  type ContratDTO,
  type ContratType,
} from '../hooks/useContratsFormateur.js';

const CURRENT_YEAR = new Date().getFullYear();

export function ContratsFormateursPage() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const { data, isLoading, error } = useContratsFormateurList(year);

  return (
    <div className="mx-auto max-w-5xl p-6">
      <header className="mb-6">
        <div className="text-primary-500 text-xs font-semibold uppercase tracking-wider">
          Paramètres
        </div>
        <h1 className="text-primary-900 text-2xl font-bold">Contrats formateurs</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Sélectionne le type de contrat et l&apos;intervenant, génère, et tu reçois le PDF prêt à
          signer dans ta boîte Gmail.
        </p>
      </header>

      {isLoading && (
        <div className="flex items-center gap-2 rounded border border-neutral-200 bg-white p-6 text-sm text-neutral-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement des formateurs…
        </div>
      )}

      {error && (
        <div className="rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          <div className="mb-1 flex items-center gap-1.5 font-semibold">
            <AlertCircle className="h-4 w-4" />
            Erreur de chargement
          </div>
          {(error as Error).message}
        </div>
      )}

      {data && (
        <>
          <GeneratorForm formateurs={data.formateurs} year={year} setYear={setYear} />
          <ContratsHistory formateurs={data.formateurs} />
        </>
      )}
    </div>
  );
}

// ─── Formulaire générateur ───────────────────────────────────────────────────

function GeneratorForm({
  formateurs,
  year,
  setYear,
}: {
  formateurs: FormateurRow[];
  year: number;
  setYear: (y: number) => void;
}) {
  const [type, setType] = useState<ContratType>('mission');
  const [formateurId, setFormateurId] = useState<string>('');
  const [apprenantZohoId, setApprenantZohoId] = useState<string>('');

  const generateCadre = useGenerateContratCadre(year);
  const generateMission = useGenerateContratMission(year);
  const isPending = generateCadre.isPending || generateMission.isPending;
  const error = generateCadre.error || generateMission.error;
  const success = generateCadre.data || generateMission.data;

  // Apprenants affiliés au formateur sélectionné
  const selectedFormateur = formateurs.find((f) => f.formateur.id === formateurId);
  const apprenantsDisponibles = useMemo(() => {
    if (!selectedFormateur) return [];
    return selectedFormateur.apprenantsAffilies.filter((a) => !a.hasMission);
  }, [selectedFormateur]);

  const canSubmit =
    !!formateurId && (type === 'cadre' || (type === 'mission' && !!apprenantZohoId)) && !isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (type === 'cadre') {
      generateCadre.mutate(
        { formateurId },
        {
          onSuccess: () => {
            // reset partial
            setApprenantZohoId('');
          },
        },
      );
    } else {
      generateMission.mutate(
        { formateurId, apprenantZohoId },
        {
          onSuccess: () => {
            setApprenantZohoId('');
          },
        },
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mb-6 rounded border border-neutral-200 bg-white p-5">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-neutral-700">
        Générer un contrat
      </h2>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Type de contrat */}
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-neutral-600">
            Type de contrat
          </label>
          <div className="flex gap-2">
            <TypeOption
              value="mission"
              current={type}
              onClick={() => setType('mission')}
              label="Convention d'intervention"
              hint="(mission, par apprenant)"
            />
            <TypeOption
              value="cadre"
              current={type}
              onClick={() => setType('cadre')}
              label="Contrat-cadre"
              hint="(annuel)"
            />
          </div>
        </div>

        {/* Année */}
        <div>
          <label
            htmlFor="year"
            className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-neutral-600"
          >
            Année
          </label>
          <select
            id="year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm"
          >
            {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        {/* Intervenant */}
        <div className="md:col-span-2">
          <label
            htmlFor="formateur"
            className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-neutral-600"
          >
            Intervenant
          </label>
          <select
            id="formateur"
            value={formateurId}
            onChange={(e) => {
              setFormateurId(e.target.value);
              setApprenantZohoId('');
            }}
            className="w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">— Choisir un intervenant —</option>
            {formateurs.map((row) => (
              <option key={row.formateur.id} value={row.formateur.id}>
                {row.formateur.nom} {row.formateur.prenom} (
                {row.formateur.role === 'assistant-pedagogique' ? 'Assistant péda' : 'Formateur'}
                {row.formateur.tarifHoraire > 0 ? ` · ${row.formateur.tarifHoraire} €/h` : ''})
              </option>
            ))}
          </select>
        </div>

        {/* Apprenant (si mission) */}
        {type === 'mission' && (
          <div className="md:col-span-2">
            <label
              htmlFor="apprenant"
              className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-neutral-600"
            >
              Apprenant affilié
            </label>
            <select
              id="apprenant"
              value={apprenantZohoId}
              onChange={(e) => setApprenantZohoId(e.target.value)}
              disabled={!formateurId}
              className="w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm disabled:bg-neutral-50 disabled:text-neutral-400"
            >
              <option value="">
                {!formateurId
                  ? "— Choisir d'abord un intervenant —"
                  : apprenantsDisponibles.length === 0
                    ? '— Aucun apprenant affilié sans convention —'
                    : '— Choisir un apprenant —'}
              </option>
              {apprenantsDisponibles.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nom.toUpperCase()} {a.prenom} ({a.nombreHeures}h)
                </option>
              ))}
            </select>
            {selectedFormateur &&
              selectedFormateur.apprenantsAffilies.length > 0 &&
              apprenantsDisponibles.length === 0 && (
                <p className="mt-1 text-xs text-amber-700">
                  Tous les apprenants affiliés ont déjà une convention. Pour en générer une
                  nouvelle, annule l&apos;existante via l&apos;historique ci-dessous.
                </p>
              )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-5 flex items-center justify-between gap-4 border-t border-neutral-100 pt-4">
        <div className="flex-1">
          {success && (
            <div className="flex items-center gap-1.5 text-sm text-green-700">
              <Mail className="h-4 w-4" />
              Contrat généré. PDF envoyé à <strong>andalous.elearning@gmail.com</strong>.
            </div>
          )}
          {error && (
            <div className="flex items-center gap-1.5 text-sm text-rose-700">
              <AlertCircle className="h-4 w-4" />
              {(error as Error).message}
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={!canSubmit}
          className="bg-primary-600 hover:bg-primary-700 inline-flex shrink-0 items-center gap-1.5 rounded px-4 py-2 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:bg-neutral-300"
        >
          {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Générer le contrat
        </button>
      </div>
    </form>
  );
}

function TypeOption({
  value,
  current,
  onClick,
  label,
  hint,
}: {
  value: ContratType;
  current: ContratType;
  onClick: () => void;
  label: string;
  hint: string;
}) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded border px-3 py-2 text-left text-sm transition-colors ${
        active
          ? 'border-primary-400 bg-primary-50 text-primary-900'
          : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300'
      }`}
    >
      <div className="font-medium">{label}</div>
      <div className="text-xs text-neutral-500">{hint}</div>
    </button>
  );
}

// ─── Historique ──────────────────────────────────────────────────────────────

function ContratsHistory({ formateurs }: { formateurs: FormateurRow[] }) {
  // Aplatit tous les contrats (cadres + missions) avec leur formateur
  const allContrats = useMemo(() => {
    const list: {
      contrat: ContratDTO;
      formateurNom: string;
      formateurPrenom: string;
    }[] = [];
    for (const row of formateurs) {
      if (row.cadre) {
        list.push({
          contrat: row.cadre,
          formateurNom: row.formateur.nom,
          formateurPrenom: row.formateur.prenom,
        });
      }
      for (const m of row.missions) {
        list.push({
          contrat: m,
          formateurNom: row.formateur.nom,
          formateurPrenom: row.formateur.prenom,
        });
      }
    }
    return list.sort(
      (a, b) =>
        new Date(b.contrat.generatedAt).getTime() - new Date(a.contrat.generatedAt).getTime(),
    );
  }, [formateurs]);

  if (allContrats.length === 0) {
    return (
      <div className="rounded border border-neutral-200 bg-white p-6 text-sm text-neutral-500">
        Aucun contrat généré pour cette année.
      </div>
    );
  }

  return (
    <section className="rounded border border-neutral-200 bg-white">
      <header className="border-b border-neutral-100 px-4 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-700">
          Historique des contrats
        </h2>
      </header>
      <ul className="divide-y divide-neutral-100">
        {allContrats.map(({ contrat, formateurNom, formateurPrenom }) => (
          <li key={contrat.id} className="flex items-center justify-between gap-4 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-neutral-900">
                  {contrat.driveFileName}
                </span>
              </div>
              <div className="mt-0.5 text-xs text-neutral-500">
                {formateurNom} {formateurPrenom} ·{' '}
                {contrat.intervenantRole === 'assistant-pedagogique'
                  ? 'Assistant péda'
                  : 'Formateur'}
                {' · '}
                Généré{' '}
                {new Date(contrat.generatedAt).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {contrat.apprenantNom &&
                  ` · pour ${contrat.apprenantNom} ${contrat.apprenantPrenom}`}
              </div>
            </div>
            <StatusBadge status={contrat.status} />
            <a
              href={
                contrat.driveWebViewUrl ??
                `/api/v1/files/${contrat.driveFileId.split('/').map(encodeURIComponent).join('/')}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 inline-flex shrink-0 items-center gap-1 text-xs"
            >
              <ExternalLink className="h-3 w-3" />
              Drive
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config = (STATUS_CONFIG[status] ?? STATUS_CONFIG.generated) as {
    cls: string;
    label: string;
    icon: React.ReactNode;
  };
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${config.cls}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

const STATUS_CONFIG: Record<string, { cls: string; label: string; icon: React.ReactNode }> = {
  generated: {
    cls: 'bg-amber-100 text-amber-800',
    label: 'généré',
    icon: <FileText className="h-3 w-3" />,
  },
  sent: {
    cls: 'bg-blue-100 text-blue-800',
    label: 'envoyé à Rafik',
    icon: <Mail className="h-3 w-3" />,
  },
  signed: {
    cls: 'bg-green-100 text-green-800',
    label: 'signé',
    icon: <Check className="h-3 w-3" />,
  },
  cancelled: {
    cls: 'bg-neutral-200 text-neutral-700',
    label: 'annulé',
    icon: <AlertCircle className="h-3 w-3" />,
  },
};
